// Stap 1: GDPR-consent + analyseperiode. PRD-06 §4 US-ONB-01.

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { OnboardingProgress } from "../OnboardingWizard";

interface Props {
  progress: OnboardingProgress;
  onAdvance: () => void;
}

export function WelcomeStep({ progress, onAdvance }: Props) {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(Boolean(progress.gdprConsentAt));
  const [years, setYears] = useState(progress.analysisPeriodYears ?? 2);
  const [showRights, setShowRights] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api.post("/v1/onboarding/consent", { consentGiven: true, analysisPeriodYears: years }),
    onSuccess: () => onAdvance(),
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!consent) return;
    submit.mutate();
  }

  return (
    <article className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
      <h2 className="text-2xl font-semibold mb-2">{t("onboarding.welcome.title", "Welkom")}</h2>
      <p className="text-neutral-700 mb-4">
        {t(
          "onboarding.welcome.intro",
          "We helpen je een compleet overzicht te krijgen van je financiële situatie. Dit duurt ongeveer 15 minuten. Je kunt op elk moment pauzeren — we slaan je voortgang op."
        )}
      </p>
      <p className="text-neutral-700 mb-6">
        {t(
          "onboarding.welcome.dataInfo",
          "Voordat we beginnen: we vragen je toestemming om je gegevens te verwerken volgens de Algemene Verordening Gegevensbescherming (AVG)."
        )}
      </p>

      <form onSubmit={onSubmit} className="space-y-5">
        <fieldset className="border border-neutral-200 rounded-xl p-4">
          <legend className="text-sm font-medium px-2">
            {t("onboarding.welcome.periodLabel", "Hoeveel jaar terug analyseren we?")}
          </legend>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
            {[1, 2, 3, 4, 5, 6].map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYears(y)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  years === y
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
                aria-pressed={years === y}
              >
                {y} {t("onboarding.welcome.yearSuffix", "jaar")}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            {t(
              "onboarding.welcome.periodHelp",
              "We adviseren minimaal 2 jaar voor een compleet beeld."
            )}
          </p>
        </fieldset>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-1"
            required
          />
          <span className="text-sm text-neutral-700">
            {t(
              "onboarding.welcome.consent",
              "Ik geef toestemming voor verwerking van mijn gegevens conform de AVG en het privacybeleid."
            )}
          </span>
        </label>

        <details
          open={showRights}
          onToggle={(e) => setShowRights((e.target as HTMLDetailsElement).open)}
          className="bg-neutral-50 rounded-lg p-3"
        >
          <summary className="cursor-pointer text-sm font-medium text-neutral-700">
            {t("onboarding.welcome.rightsTitle", "Uw rechten onder de AVG")}
          </summary>
          <ul className="mt-2 ml-4 text-xs text-neutral-600 list-disc space-y-1">
            <li>{t("onboarding.welcome.right.access", "Recht op inzage")}</li>
            <li>{t("onboarding.welcome.right.correct", "Recht op correctie")}</li>
            <li>{t("onboarding.welcome.right.delete", "Recht op verwijdering")}</li>
            <li>{t("onboarding.welcome.right.portable", "Recht op dataportabiliteit")}</li>
            <li>{t("onboarding.welcome.right.revoke", "Recht om toestemming in te trekken")}</li>
          </ul>
        </details>

        {errorKey && (
          <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            {t(errorKey)}
          </div>
        )}

        <button
          type="submit"
          disabled={!consent || submit.isPending}
          className="w-full px-4 py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg"
        >
          {submit.isPending
            ? t("loading", "Laden…")
            : t("onboarding.welcome.continue", "Akkoord en doorgaan")}
        </button>
      </form>
    </article>
  );
}
